/**
 * V1A DERIVED acquisition source precedence (spec §7.5 / FR-24).
 * CAMPAIGN > REFERRAL > ORGANIC > DIRECT > OTHER
 */
export type CustomerAcquisitionSource =
  | 'ORGANIC'
  | 'CAMPAIGN'
  | 'REFERRAL'
  | 'DIRECT'
  | 'OTHER';

const SOURCE_PRECEDENCE: Record<CustomerAcquisitionSource, number> = {
  CAMPAIGN: 50,
  REFERRAL: 40,
  ORGANIC: 30,
  DIRECT: 20,
  OTHER: 10,
};

export interface ResolveCustomerAcquisitionSourceInput {
  readonly urlSearchParams: URLSearchParams;
  readonly documentReferrer?: string | null;
  readonly hasValidDiscoveryAttribution?: boolean;
  readonly unclassifiedAcquisitionMarker?: boolean;
}

function readTrimmedParam(params: URLSearchParams, key: string): string | null {
  const raw = params.get(key);
  if (raw === null) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function hasCampaignMarker(params: URLSearchParams): boolean {
  const campaign = readTrimmedParam(params, 'utm_campaign');
  if (campaign !== null) {
    return true;
  }
  const medium = readTrimmedParam(params, 'utm_medium')?.toLowerCase() ?? '';
  if (medium === 'cpc' || medium === 'email' || medium === 'paid' || medium === 'paidsearch') {
    return readTrimmedParam(params, 'utm_source') !== null;
  }
  return false;
}

function hasReferralMarker(
  params: URLSearchParams,
  documentReferrer?: string | null,
): boolean {
  const medium = readTrimmedParam(params, 'utm_medium')?.toLowerCase() ?? '';
  if (medium === 'referral' || medium === 'partner') {
    return true;
  }
  const ref = readTrimmedParam(params, 'ref') ?? readTrimmedParam(params, 'referral');
  if (ref !== null) {
    return true;
  }
  const referrer = documentReferrer?.trim() ?? '';
  if (referrer.length === 0) {
    return false;
  }
  try {
    const refUrl = new URL(referrer);
    if (refUrl.origin === (typeof window !== 'undefined' ? window.location.origin : '')) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function hasUnclassifiedAcquisitionMarker(params: URLSearchParams): boolean {
  const gclid = readTrimmedParam(params, 'gclid');
  const fbclid = readTrimmedParam(params, 'fbclid');
  const msclkid = readTrimmedParam(params, 'msclkid');
  return gclid !== null || fbclid !== null || msclkid !== null;
}

export function resolveCustomerAcquisitionSource(
  input: ResolveCustomerAcquisitionSourceInput,
): CustomerAcquisitionSource {
  const candidates: CustomerAcquisitionSource[] = [];
  if (hasCampaignMarker(input.urlSearchParams)) {
    candidates.push('CAMPAIGN');
  }
  if (hasReferralMarker(input.urlSearchParams, input.documentReferrer)) {
    candidates.push('REFERRAL');
  }
  if (input.hasValidDiscoveryAttribution === true) {
    candidates.push('ORGANIC');
  }
  if (
    input.unclassifiedAcquisitionMarker === true ||
    hasUnclassifiedAcquisitionMarker(input.urlSearchParams)
  ) {
    candidates.push('OTHER');
  }
  if (candidates.length === 0) {
    return 'DIRECT';
  }
  return candidates.reduce((best, next) =>
    SOURCE_PRECEDENCE[next] > SOURCE_PRECEDENCE[best] ? next : best,
  );
}

/** Later discovery ORGANIC must not downgrade an existing CAMPAIGN/REFERRAL latch. */
export function mergeCustomerAcquisitionSource(
  existing: CustomerAcquisitionSource | null | undefined,
  incoming: CustomerAcquisitionSource,
): CustomerAcquisitionSource {
  if (existing === null || existing === undefined) {
    return incoming;
  }
  return SOURCE_PRECEDENCE[incoming] >= SOURCE_PRECEDENCE[existing]
    ? incoming
    : existing;
}

export function readUrlAcquisitionSignals(
  urlSearchParams: URLSearchParams,
  documentReferrer?: string | null,
): ResolveCustomerAcquisitionSourceInput {
  return {
    urlSearchParams,
    documentReferrer,
    hasValidDiscoveryAttribution: false,
    unclassifiedAcquisitionMarker: hasUnclassifiedAcquisitionMarker(urlSearchParams),
  };
}
